import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { twMerge } from 'tailwind-merge';

import MaskedInput from 'components/shared/others/masked-input/masked-input';
import IconButton from 'components/partials/button/icon-button';
import DatePickerCalendar from './date-picker-calendar';

import { useOpen } from 'hooks/use-open';
import { useClickOutside } from 'hooks/use-click-outside';

import {
  parseTypedDate,
  formatDateValue,
  getDateFormatError,
} from './helpers/date-format';

import Icons from 'assets/icons';

/** Gap between the input and the calendar, matching the old `mt-1` offset. */
const CALENDAR_GAP_PX = 4;

/**
 * Clears the shared `Modal`'s `z-[701]` — matches the `zIndex: 9999` every
 * `Select` inside a modal already sets on its own `menuPortalTarget` menu.
 */
const CALENDAR_Z_INDEX = 9999;

interface CalendarPosition {
  top: number;
  left: number;
}

export interface DatePickerProps {
  label?: string;
  labelFor: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  className?: string;
  customClass?: string;
  inputCustomClass?: string;
  'data-testid'?: string;
}

const DatePicker = ({
  label,
  labelFor,
  value,
  onChange,
  onBlur,
  placeholder = 'mm.dd.yyyy',
  required,
  errorMessage,
  disabled,
  className,
  customClass,
  inputCustomClass,
  'data-testid': dataTestId = labelFor,
}: DatePickerProps) => {
  const { isOpen, handleClose, handleToggle } = useOpen();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = parseTypedDate(value);
  const [viewDate, setViewDate] = useState<Dayjs>(
    () => selectedDate ?? dayjs()
  );
  const [inputResetKey, setInputResetKey] = useState(0);
  const [calendarPosition, setCalendarPosition] =
    useState<CalendarPosition | null>(null);
  const displayError = getDateFormatError(value) ?? errorMessage;

  // Portaled to `document.body` (see the render below), so a click inside it
  // is outside `containerRef` by DOM position alone — check both.
  const outsideClickRefs = useMemo(() => [containerRef, calendarRef], []);

  useClickOutside(outsideClickRefs, handleClose, isOpen);

  // `position: fixed` escapes the shared `Modal`'s `overflow-hidden`/stacking
  // context, but only tracks the viewport — re-measure on scroll (capture
  // phase, so a scroll inside the modal's own scroll container is caught too)
  // and resize to keep the calendar anchored to the input while open.
  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect();

      if (rect) {
        setCalendarPosition({
          top: rect.bottom + CALENDAR_GAP_PX,
          left: rect.left,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleToggleCalendar = () => {
    if (!isOpen) {
      setViewDate(selectedDate ?? dayjs());
    }

    handleToggle();
  };

  const handleSelectDate = (date: Dayjs) => {
    onChange(formatDateValue(date));
    setInputResetKey((prev) => prev + 1);
    handleClose();
  };

  return (
    <div
      className={twMerge('relative', className)}
      ref={containerRef}
      data-testid={dataTestId}
    >
      <MaskedInput
        // react-text-mask only reads `value` as the initial `defaultValue`; remount
        // (via key) to reflect a value picked in the calendar instead of typed.
        key={inputResetKey}
        mask="mmddyyyyDot"
        showMask={false}
        label={label}
        labelFor={labelFor}
        labelInTop
        required={required}
        value={value}
        placeholder={placeholder}
        errorsNames={displayError ? { message: displayError } : {}}
        onChange={(event) => onChange(event.target.value)}
        onBlur={(event) => onBlur?.(event)}
        disabled={disabled}
        customClass={customClass}
        inputCustomClass={inputCustomClass}
        endAdornment={
          <IconButton
            icon={<Icons.Calendar />}
            variant="reserve-text"
            size="sm"
            aria-label="Open calendar"
            onClick={handleToggleCalendar}
            disabled={disabled}
            data-testid={`${dataTestId}-toggle`}
          />
        }
        data-testid={`${dataTestId}-input`}
      />

      {isOpen &&
        calendarPosition &&
        createPortal(
          <DatePickerCalendar
            ref={calendarRef}
            style={{
              position: 'fixed',
              top: calendarPosition.top,
              left: calendarPosition.left,
              zIndex: CALENDAR_Z_INDEX,
            }}
            viewDate={viewDate}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            onViewDateChange={setViewDate}
            data-testid={`${dataTestId}-calendar`}
          />,
          document.body
        )}
    </div>
  );
};

export default DatePicker;
